/**
 * TimeSelect — reliable cross-platform HH:MM picker.
 *
 * Iter72.2 — the native ``<input type="time">`` picker on mobile Chrome
 * and desktop was burying its "Set" button behind the Radix Dialog
 * overlay, so admins could type a new time but had no visible way to
 * commit it. Replacing it with two Shadcn Select dropdowns (hours
 * 00-23, minutes 00-55 in 5-min steps) means the value commits on
 * every tap and is always visible — no OS/browser dependency.
 *
 * Accepts and emits a canonical ``HH:MM`` string so callers (newShift
 * state, PUT payload, duration calc) stay unchanged.
 */
import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

export function TimeSelect({ value, onChange, testIdPrefix }) {
  const [hh, mm] = useMemo(() => {
    const safe = typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) ? value : '08:00';
    return safe.split(':');
  }, [value]);

  // If the current minutes value isn't on our 5-min grid (e.g. an
  // existing shift saved as 08:07), include it as a one-off option so
  // the dropdown still shows the real value instead of silently
  // snapping it on open.
  const minuteOptions = useMemo(() => {
    if (MINUTES.includes(mm)) return MINUTES;
    return [...MINUTES, mm].sort();
  }, [mm]);

  const emit = (nextHh, nextMm) => {
    onChange(`${nextHh}:${nextMm}`);
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={hh} onValueChange={(v) => emit(v, mm)}>
        <SelectTrigger
          className="bg-[#1A1A1A] border-white/10 w-20"
          data-testid={testIdPrefix ? `${testIdPrefix}-hours` : undefined}
          aria-label="Hours"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {HOURS.map((h) => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-white/60 select-none">:</span>
      <Select value={mm} onValueChange={(v) => emit(hh, v)}>
        <SelectTrigger
          className="bg-[#1A1A1A] border-white/10 w-20"
          data-testid={testIdPrefix ? `${testIdPrefix}-minutes` : undefined}
          aria-label="Minutes"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {minuteOptions.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default TimeSelect;
